import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Filter, Download, Eye, Edit, Search, Send, X, CheckCircle2, Trash2, AlertCircle, Forward } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { Textarea } from '../components/ui/Textarea';
import { Tooltip } from '../components/ui/Tooltip';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { TicketFormModal } from '../components/tickets/TicketFormModal';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { useNotifications } from '../contexts/NotificationContext';
import { useRBAC } from '../contexts/RBACContext';
import { ticketsStore } from '../services/ticketsStore';
import { ticketChannels, ticketPeople, ticketStatuses } from '../data/ticketOptions';
import { canEditTicket, getPersonName } from '../utils/ticketPermissions';
import { Ticket, TicketPerson, TicketStatus } from '../types/ticket';
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'description',
  label: 'Descrizione'
}, {
  key: 'client',
  label: 'Azienda'
}, {
  key: 'channel',
  label: 'Canale'
}, {
  key: 'recipient',
  label: 'Destinatario'
}, {
  key: 'deadline',
  label: 'Scadenza'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const statusVariant: Record<TicketStatus, 'warning' | 'info' | 'success'> = {
  Aperto: 'warning',
  Sospeso: 'info',
  Chiuso: 'success'
};
function formatDate(date: string): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('it-IT');
}
export function TicketsPage() {
  const {
    addNotification
  } = useNotifications();
  const {
    currentUser
  } = useRBAC();
  const me = getPersonName(currentUser.name);
  const [tickets, setTickets] = useState<Ticket[]>(ticketsStore.getAll());
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Ticket | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [forwardSuccess, setForwardSuccess] = useState(false);
  const [forwardAssignee, setForwardAssignee] = useState<TicketPerson | ''>('');
  useEffect(() => ticketsStore.subscribe(() => setTickets(ticketsStore.getAll())), []);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(tickets);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['description', 'client', 'code', 'recipient']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleCreate = () => {
    setEditingTicket(null);
    setIsFormOpen(true);
  };
  const handleEdit = (item: Ticket) => {
    setEditingTicket(item);
    setIsFormOpen(true);
  };
  const handleViewDetail = (item: Ticket) => {
    setSelectedItem(item);
    setIsDetailOpen(true);
  };
  const handleDelete = (item: Ticket) => {
    setSelectedItem(item);
    setIsDeleteConfirmOpen(true);
  };
  const handleForward = (item: Ticket) => {
    setSelectedItem(item);
    setForwardAssignee('');
    setIsForwardModalOpen(true);
  };
  const confirmDelete = () => {
    if (!selectedItem) return;
    setDeleteSuccess(true);
    setTimeout(() => {
      ticketsStore.remove(selectedItem.id);
      setDeleteSuccess(false);
      setIsDeleteConfirmOpen(false);
      addNotification('success', 'Ticket eliminato', 'Il ticket è stato eliminato con successo.');
    }, 1500);
  };
  const handleConfirmForward = () => {
    if (!selectedItem || !forwardAssignee) return;
    setForwardSuccess(true);
    setTimeout(() => {
      ticketsStore.update(selectedItem.id, {
        recipient: forwardAssignee
      });
      setForwardSuccess(false);
      setIsForwardModalOpen(false);
      addNotification('success', 'Ticket inoltrato', `Il ticket è stato assegnato a ${forwardAssignee}.`);
    }, 1500);
  };
  const handleAddComment = () => {
    addNotification('success', 'Commento aggiunto', 'Il tuo commento è stato salvato.');
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  const selectedEditable = selectedItem ? canEditTicket(selectedItem, me) : false;
  return <Layout data-id="element-6022" data-mp="3ltn52wyudlp6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-6023" data-mp="3lt3kt9lpyl7z">
        <div data-id="element-6024" data-mp="37tknlbug4uag">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6025" data-mp="37ssty6ket6mt">Ticket</h1>
          <p className="text-slate-500 mt-1" data-id="element-6026" data-mp="3cui47jzt5g9u">
            Gestione richieste e segnalazioni.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-6027" data-mp="2xxr1p1uu7og0">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-6029" data-mp="32x3zuir5shxw" />} data-id="element-6028" data-mp="32varmz0wdpns">
            Esporta
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-6031" data-mp="112mo0okcd07h" />} data-id="element-6030" data-mp="w2qjmo7qf6re">
            Nuovo ticket
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-6032" data-mp="zo1xyxzsdp55">
        <div className="p-4 flex flex-col gap-4" data-id="element-6033" data-mp="un4kgypq1j9z">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-6034" data-mp="umcm5g4qp7g0">
            <div className="flex-1" data-id="element-6035" data-mp="pnt1rk5so3pr">
              <Input placeholder="Cerca ticket..." startContent={<Search className="h-4 w-4" data-id="element-6037" data-mp="loap1bo4myy0" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-6036" data-mp="poumkgcm44ko" />
            </div>
            <div className="flex gap-2" data-id="element-6038" data-mp="1dep0l18o3z6i">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-6040" data-mp="14k0td5jj87f2" />} data-id="element-6039" data-mp="18nfa5yfaortk">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-6041" data-mp="1wchu5cxeiwr8">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="tickets" lockedColumns={['code']} data-id="element-6042" data-mp="3jzvus2uv0ubw" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100" data-id="element-6043" data-mp="1spwjp8g292j">
              <Select label="Canale" value={filters.channel || ''} onChange={e => setFilter('channel', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...ticketChannels.map(c => ({
            value: c,
            label: c
          }))]} data-id="element-6044" data-mp="6q7y2uk4tm0i" />
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...ticketStatuses.map(s => ({
            value: s,
            label: s
          }))]} data-id="element-6045" data-mp="pi0och7tgt4j" />
              <Select label="Destinatario" value={filters.recipient || ''} onChange={e => setFilter('recipient', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...ticketPeople.map(p => ({
            value: p,
            label: p
          }))]} data-id="element-6046" data-mp="9e0dz1cjn4uy" />
              <Select label="Azienda / Interno" value={filters.scope || ''} onChange={e => setFilter('scope', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Azienda',
            label: 'Azienda'
          }, {
            value: 'Interno',
            label: 'Interno'
          }]} data-id="element-6047" data-mp="3mno9w7fg695a" />
              <div className="col-span-full flex justify-end" data-id="element-6048" data-mp="2fzh0hx796j97">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-6050" data-mp="2b1yo1wu11lwg" />} data-id="element-6049" data-mp="2ksa76qma81nx">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-6051" data-mp="2ypp045bqurkn">
        <div className="overflow-x-auto" data-id="element-6052" data-mp="33pl3q3gj4gzo">
          <table className="w-full text-left border-collapse table-auto" data-id="element-6053" data-mp="33oa0c4qzxb93">
            <thead data-id="element-6054" data-mp="2ostz9fs00f37">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-6055" data-mp="2ovgbhspu3irr">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 whitespace-nowrap ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-6056" data-mp="1ie2xzayyqs8n">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6057" data-mp="22v85mblysmat">
              {paginatedData.map(item => {
              const editable = canEditTicket(item, me);
              return <tr key={item.id} className="hover:bg-slate-50/80 transition-colors" data-id="element-6058" data-mp="1tti58ga3ud0w">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500 whitespace-nowrap" data-id="element-6059" data-mp="1jygjzviy9v3l">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm font-medium text-slate-900 max-w-xs" data-id="element-6060" data-mp="3jaj8jk6flfo4">
                        <span className="line-clamp-1" data-id="element-6061" data-mp="17mec3w5kjkaw">{item.description}</span>
                      </td>}
                    {visibleColumns.includes('client') && <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap" data-id="element-6062" data-mp="1hch5hxglr09r">
                        {item.scope === 'Azienda' ? item.client : <span className="text-slate-400" data-id="element-6063" data-mp="1qcmc45dz1umv">Interno</span>}
                      </td>}
                    {visibleColumns.includes('channel') && <td className="px-4 py-4" data-id="element-6064" data-mp="20blc6zko8hy9">
                        <Badge variant={item.channel === 'Urgente' ? 'danger' : 'neutral'} data-id="element-6065" data-mp="2098vsqukafnb">
                          {item.channel}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('recipient') && <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap" data-id="element-6066" data-mp="2121mujxcj31b">
                        {item.recipient}
                      </td>}
                    {visibleColumns.includes('deadline') && <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap" data-id="element-6067" data-mp="2ezzyvio72ndn">
                        {formatDate(item.deadline)}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-6068" data-mp="2owuxe1kgwnrx">
                        <Badge variant={statusVariant[item.status]} data-id="element-6069" data-mp="2tpea6wbbzevo">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-6070" data-mp="32rdsdry30p07">
                        <div className="flex items-center gap-1" data-id="element-6071" data-mp="37odwhxbdfhoo">
                          <Tooltip content="Visualizza ticket" data-id="element-6072" data-mp="37mkpggwuivg5">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleViewDetail(item)} aria-label="Visualizza ticket" data-id="element-6073" data-mp="3htna525u0dyh">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-6074" data-mp="33v5od9zhlbj7" />
                            </Button>
                          </Tooltip>
                          <Tooltip content={editable ? 'Modifica ticket' : 'Modificabile solo da chi lo ha emesso o dal destinatario'} data-id="element-6075" data-mp="3vjz313a8qgqo">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} disabled={!editable} aria-label="Modifica ticket" data-id="element-6076" data-mp="n58phuqiay5w">
                              <Edit className={`h-4 w-4 ${editable ? 'text-seform-orange' : 'text-slate-300'}`} data-id="element-6077" data-mp="3q0x28v4lamjd" />
                            </Button>
                          </Tooltip>
                          <Tooltip content={editable ? 'Inoltra ticket' : 'Inoltrabile solo da chi lo ha emesso o dal destinatario'} data-id="element-6078" data-mp="lmyxkeptf6uc">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleForward(item)} disabled={!editable} aria-label="Inoltra ticket" data-id="element-6079" data-mp="gte0cow1qgw7">
                              <Forward className={`h-4 w-4 ${editable ? 'text-blue-500' : 'text-slate-300'}`} data-id="element-6080" data-mp="2p2kefnhani9d" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Elimina ticket" data-id="element-6081" data-mp="2b32m4y4shmv9">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} aria-label="Elimina ticket" data-id="element-6082" data-mp="2b3kpeepvd1co">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-6083" data-mp="2g4rvm9cd5sh8" />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>}
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-6084" data-mp="7dy659h65638">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-6085" data-mp="7cmu6mdkks11" />
        </div>
      </Card>

      {/* Create / Edit — same dialog as the global "+" button */}
      <TicketFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} ticket={editingTicket} data-id="element-6086" data-mp="3ksuj4kdezfgn" />

      {/* Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} title={`Ticket ${selectedItem?.code ?? ''}`} size="lg" data-id="element-6087" data-mp="3pqm938ui0ri7">
        {selectedItem && <div className="space-y-6" data-id="element-6088" data-mp="2x1yutx8632e5">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-6089" data-mp="32230stbg8eq4">
              <div className="flex justify-between items-start gap-4 mb-3" data-id="element-6090" data-mp="1j7i2kklzlded">
                <p className="font-semibold text-seform-navy" data-id="element-6091" data-mp="1j5fe450hfsbr">
                  {selectedItem.description}
                </p>
                <div className="flex gap-2 flex-shrink-0" data-id="element-6092" data-mp="1a6k7irtj0g7x">
                  <Badge variant={selectedItem.channel === 'Urgente' ? 'danger' : 'neutral'} data-id="element-6093" data-mp="1a96dwo4okpok">
                    {selectedItem.channel}
                  </Badge>
                  <Badge variant={statusVariant[selectedItem.status]} data-id="element-6094" data-mp="vhnsubwys4yc">
                    {selectedItem.status}
                  </Badge>
                </div>
              </div>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm" data-id="element-6095" data-mp="mhhd0jdsmsw0">
                <div data-id="element-6096" data-mp="mfegrhm1s3iu">
                  <dt className="text-xs font-semibold text-slate-400 uppercase" data-id="element-6097" data-mp="hiejnw76eb3v">
                    Azienda / Interno
                  </dt>
                  <dd className="text-slate-700 mt-0.5" data-id="element-6098" data-mp="ckmiscojzenj">
                    {selectedItem.scope === 'Azienda' ? selectedItem.client : 'Interno'}
                  </dd>
                </div>
                <div data-id="element-6099" data-mp="25czwozwn1dha">
                  <dt className="text-xs font-semibold text-slate-400 uppercase" data-id="element-6100" data-mp="1rfbanoqu40an">
                    Destinatario
                  </dt>
                  <dd className="text-slate-700 mt-0.5" data-id="element-6101" data-mp="1wdcsk6cixtkj">
                    {selectedItem.recipient}
                  </dd>
                </div>
                <div data-id="element-6102" data-mp="1hnorn6898wxc">
                  <dt className="text-xs font-semibold text-slate-400 uppercase" data-id="element-6103" data-mp="1mfzwmnhur8wr">
                    Per conoscenza
                  </dt>
                  <dd className="text-slate-700 mt-0.5" data-id="element-6104" data-mp="18qeibqezzhv2">
                    {selectedItem.cc.length > 0 ? selectedItem.cc.join(', ') : '-'}
                  </dd>
                </div>
                <div data-id="element-6105" data-mp="yt01vqt2qt2r">
                  <dt className="text-xs font-semibold text-slate-400 uppercase" data-id="element-6106" data-mp="13ph05dtzmntk">
                    Data di scadenza
                  </dt>
                  <dd className="text-slate-700 mt-0.5" data-id="element-6107" data-mp="k4qak73w7b27">
                    {formatDate(selectedItem.deadline)}
                  </dd>
                </div>
                <div data-id="element-6108" data-mp="u1v08gwy5onr">
                  <dt className="text-xs font-semibold text-slate-400 uppercase" data-id="element-6109" data-mp="u2eennne1ux1">
                    Emesso da
                  </dt>
                  <dd className="text-slate-700 mt-0.5" data-id="element-6110" data-mp="p1qh0j4nmftu">
                    {selectedItem.createdBy} · {formatDate(selectedItem.date)}
                  </dd>
                </div>
                {selectedItem.notes && <div className="col-span-2" data-id="element-6111" data-mp="yzekbxgbsvnm">
                    <dt className="text-xs font-semibold text-slate-400 uppercase" data-id="element-6112" data-mp="1crkun2wss8f5">
                      Note
                    </dt>
                    <dd className="text-slate-700 mt-0.5" data-id="element-6113" data-mp="17vm87kv150hd">
                      {selectedItem.notes}
                    </dd>
                  </div>}
              </dl>
            </div>

            {selectedItem.comments.length > 0 && <div className="space-y-4 max-h-[300px] overflow-y-auto custom-scrollbar p-2" data-id="element-6114" data-mp="1u9e0muld1p69">
                {selectedItem.comments.map((comment, idx) => <div key={idx} className="flex gap-3" data-id="element-6115" data-mp="23b53lroinhwf" data-mpi={idx}>
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 flex-shrink-0" data-id="element-6116" data-mp="23alkw8r1kphc">
                      {comment.user.charAt(0)}
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-3 flex-1 shadow-sm" data-id="element-6117" data-mp="1bhuzyuet6n7e">
                      <div className="flex justify-between items-center mb-1" data-id="element-6118" data-mp="16j1i6pshoa1f">
                        <span className="font-bold text-xs text-seform-navy" data-id="element-6119" data-mp="1kdkdyidxgfar">
                          {comment.user}
                        </span>
                        <span className="text-[10px] text-slate-400" data-id="element-6120" data-mp="rvgfgwzj0cu1">
                          {comment.date}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700" data-id="element-6121" data-mp="nsk3vq8elgzl">{comment.text}</p>
                    </div>
                  </div>)}
              </div>}

            <div className="flex gap-2 items-start pt-4 border-t border-slate-100" data-id="element-6122" data-mp="iihd6hiztas0">
              <div className="flex-1" data-id="element-6123" data-mp="3tlbr3bfyw0d">
                <Textarea rows={2} aria-label="Nuovo commento" data-id="element-6124" data-mp="3qflhkr2to32" />
              </div>
              <Button onClick={handleAddComment} startContent={<Send className="h-4 w-4" data-id="element-6126" data-mp="whh1fo2lfnnn" />} data-id="element-6125" data-mp="8p8zdea9zh3r">
                Invia
              </Button>
            </div>

            {selectedEditable && <div className="flex justify-end" data-id="element-6127" data-mp="mj9orhfc7rrg">
                <Button variant="flat" onClick={() => {
            setIsDetailOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-6129" data-mp="37bbrspce6k5n" />} data-id="element-6128" data-mp="mii5n62xfmi2">
                  Modifica
                </Button>
              </div>}
          </div>}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Conferma eliminazione" size="sm" data-id="element-6130" data-mp="1dpqdbaot8zvn">
        <AnimatePresence mode="wait" data-id="element-6131" data-mp="18x704eu1c6rd">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex flex-col items-center justify-center py-12" data-id="element-6132" data-mp="13zxdf6y16978">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-6133" data-mp="q0xorzaipub2">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-6134" data-mp="l3p6zzta7sac" />
              </div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-6135" data-mp="2kdxb0b9elmrb">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-6136" data-mp="2mzwk1tszfpdr">
              <div className="space-y-6" data-id="element-6137" data-mp="2rxppgkmgofcz">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-6138" data-mp="2rweoeskehlpk">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-6139" data-mp="2wpg596463vbs" />
                  <p className="text-sm text-red-700" data-id="element-6140" data-mp="31mzl2agikg5u">
                    Attenzione: questa azione è irreversibile. Il ticket{' '}
                    <strong data-id="element-6141" data-mp="242db0q6iy6rc">{selectedItem?.code}</strong> verrà eliminato
                    definitivamente.
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-6142" data-mp="2dy6vcvqhj4jo">
                  <Button variant="flat" color="default" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-6143" data-mp="2e0t24t5jxg0u">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} data-id="element-6144" data-mp="3ubx1e81602vy">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Forward Modal — reassigns the recipient */}
      <Modal isOpen={isForwardModalOpen} onClose={() => setIsForwardModalOpen(false)} title="Inoltra ticket" size="sm" data-id="element-6145" data-mp="5rcvq0bsdvet">
        <AnimatePresence mode="wait" data-id="element-6146" data-mp="tthh0f39imv">
          {forwardSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex flex-col items-center justify-center py-12" data-id="element-6147" data-mp="eq6vfzkez1wl">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-6148" data-mp="jj01qrvj6ias">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-6149" data-mp="ydecy68usnew" />
              </div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-6150" data-mp="ti8tde2xbktz">
                Ticket inoltrato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-6151" data-mp="1h2rkidjzw3h7">
              <div className="space-y-6" data-id="element-6152" data-mp="134ji3q46w9yn">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-6153" data-mp="133rw970e9qlk">
                  <p className="text-sm font-medium text-blue-900" data-id="element-6154" data-mp="181tclk6frbtc">
                    Stai inoltrando il ticket:
                  </p>
                  <p className="text-sm text-blue-700 mt-1" data-id="element-6155" data-mp="tcdoyd27bmhv">
                    {selectedItem?.code} — attuale destinatario:{' '}
                    {selectedItem?.recipient}
                  </p>
                </div>
                <Select label="Nuovo destinatario" value={forwardAssignee} onChange={e => setForwardAssignee(e.target.value as TicketPerson | '')} options={[{
              value: '',
              label: 'Seleziona...'
            }, ...ticketPeople.filter(p => p !== selectedItem?.recipient).map(p => ({
              value: p,
              label: p
            }))]} required data-id="element-6156" data-mp="pcdc2wur3jcx" />
                <div className="flex justify-end gap-3 pt-4" data-id="element-6157" data-mp="3ipojx5odk01i">
                  <Button variant="flat" color="default" onClick={() => setIsForwardModalOpen(false)} data-id="element-6158" data-mp="3drdaux5wltkc">
                    Annulla
                  </Button>
                  <Button onClick={handleConfirmForward} disabled={!forwardAssignee} startContent={<Forward className="h-4 w-4" data-id="element-6160" data-mp="bny55949uya" />} data-id="element-6159" data-mp="aadc4mxw6v99">
                    Inoltra
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}